import React from 'react';
import { useSidebar } from '../../context/SidebarContext';
import Sparkle from '../../assets/sparcle.svg';

interface ResearchItem {
  title: string;
  description: string;
  source: string;
  category?: string;
}

interface ResearchCardProps {
  items: ResearchItem[];
  className?: string;
}

const tagColors = [
  { category: 'Industry & Market Position', bg: 'bg-[#2196F3]', text: 'text-[#FFFFFF]' },
  { category: 'Company Size & Team Composition', bg: 'bg-[#4CAF50]', text: 'text-[#FFFFFF]' },
  { category: 'Countries Served', bg: 'bg-[#9C27B0]', text: 'text-[#FFFFFF]' },
  { category: 'Funding & Revenue', bg: 'bg-[#F44336]', text: 'text-[#FFFFFF]' },
  { category: 'Growth Rate & TAM', bg: 'bg-[#FF9800]', text: 'text-[#FFFFFF]' },
  { category: 'Technology Stack & Infrastructure', bg: 'bg-[#607D8B]', text: 'text-[#FFFFFF]' },
  { category: 'Technological Maturity & Scalability', bg: 'bg-[#795548]', text: 'text-[#FFFFFF]' },
  { category: 'Competitive Landscape', bg: 'bg-[#E91E63]', text: 'text-[#FFFFFF]' },
  { category: 'Organizational Complexity & Decision-Making', bg: 'bg-[#673AB7]', text: 'text-[#FFFFFF]' },
  { category: 'Budget Availability & Pain Points', bg: 'bg-[#FF5722]', text: 'text-[#FFFFFF]' },
  { category: 'Customer Base & Buyer Personas', bg: 'bg-[#009688]', text: 'text-[#FFFFFF]' },
  { category: 'Potential for Upselling & Lifetime Value', bg: 'bg-[#8BC34A]', text: 'text-[#FFFFFF]' },
  { category: 'Product Complexity Alignment', bg: 'bg-[#3F51B5]', text: 'text-[#FFFFFF]' },
  // Default color for any other category
  { category: 'default', bg: 'bg-[#78909C]', text: 'text-[#FFFFFF]' }
];

const truncateSource = (source: string): string => {
  if (!source) return '';
  const url = source.replace(/^https?:\/\//, '');
  if (url.length <= 50) return url;
  return url.substring(0, 50) + '...';
};

const getTagColor = (category: string) => {
  const colorMatch = tagColors.find(color => color.category === category);
  return colorMatch || tagColors[tagColors.length - 1]; // Returns default color if no match
};

const ResearchCard: React.FC<ResearchCardProps> = ({ items, className = '' }) => {
  const { isSidebarOpen } = useSidebar();

  return (
    <div className={`flex flex-col items-start w-full ${className}`}>
      <div className="text-[20px] font-normal text-[#222222] mb-[16px]">
        Research
      </div>

      {items.length > 0 ? (
        <div className="flex flex-col w-full gap-4">
          {items.map((item, index) => (
            <div 
              key={index}
              className="flex flex-col bg-[#FFFFFF] rounded-lg border border-[#EEEEEE] overflow-hidden"
            >
              <div className="flex items-start justify-between gap-2 p-4">
                <div className="w-[52px] h-[52px] rounded-lg flex items-center justify-center p-1">
                  <span className={`w-[44px] h-[44px] rounded-lg flex items-center justify-center ${getTagColor(item.title).bg}`}>
                    <img src={Sparkle} alt="SparkleStick" className="w-[18px] h-[18px]" />
                  </span>
                </div>



                <div className="flex flex-col gap-2 text-left pl-2 w-full">


                  <div className="flex-1">
                    <h3 className="text-[14px] font-normal text-[#222222] mb-1">
                      {item.title}
                    </h3>
                    <div className="flex gap-1 items-start w-full">
                      <span className='text-[#5C5E63] text-sm'>•&nbsp;</span>
                      <p className="text-sm text-[#5C5E63] font-light">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 border border-[#EEEEEE] w-fit px-2 py-1 rounded-lg">
                    <span className="text-xs font-light text-[#222222]">Source : </span>
                    <a 
                      href={item.source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-light text-[#000000] hover:text-[#266DF0] truncate"
                    >
                      {truncateSource(item.source)}
                    </a>
                  </div>
                </div>
              </div>
            </div>

          ))}
        </div>
      ) : (
        <div className="flex flex-col h-full w-full items-center justify-center py-8 text-center gap-3">
          <div className="flex items-center justify-center w-[400px] h-[34px] bg-[#FAFAFA] rounded-lg border border-[#EEEEEE]" />
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="text-[16px] font-semibold text-[#222222]">No research available for this lead yet.</span>
            <span className="text-[14px] font-medium text-[#7A7A7A]">We'll update once we find relevant insights.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchCard; 